import { useMemo, useState } from 'react';
import { ScanSearch } from 'lucide-react';
import { Hint } from '@/components/Hint';
import { Can } from '@/auth/guards';
import { cn } from '@/lib/utils';
import {
  createOrderTask,
  type DocumentGapCode,
  type OpenAgentTask,
  type OrderDocHint,
  type OrdersOverview,
  type OverviewReceiving,
  type ReceivingNeedCode,
} from '@/api/orders';
import {
  JourneyName, PendingNudge, RowAction, UploadDocumentButton, VendorDraftButton,
  WorkEmpty, WorkFootnote, WorkRow, WorkSection,
} from './workLib';

interface DocProblem {
  key: string;
  rcv: OverviewReceiving;
  problem: string;
  /** What an upload of this row most likely is — pre-picks the doc hint. */
  hint: OrderDocHint;
  /** What a find_order_doc nudge should hunt. */
  target: 'invoice' | 'ordered';
  /** True when the mailbox was already searched — only a human can source it. */
  humanOnly: boolean;
}

// What an upload / re-hunt for each need code means. Keyed on the CODE — the
// server's documents count filters on the same set, so a rewording of the
// prose can never re-route or re-count a row. Absent codes are deliberate:
// receipt_not_mirrored is the run's own mirror work, and duty_unattributed
// belongs to the Duties view (a carrier-shaped problem — "Ask vendor" and an
// order-form hint were the wrong affordances for a DHL bill).
const NEED_ROUTES: Partial<Record<ReceivingNeedCode, { hint: OrderDocHint; target: 'invoice' | 'ordered' }>> = {
  invoice_missing: { hint: 'invoice', target: 'invoice' },
  invoice_no_lines: { hint: 'invoice', target: 'invoice' },
  invoice_total_missing: { hint: 'invoice', target: 'invoice' },
};

const GAP_ROUTES: Record<DocumentGapCode, { hint: OrderDocHint; target: 'invoice' | 'ordered' }> = {
  ordered_missing: { hint: 'order_form', target: 'ordered' },
  ordered_partial: { hint: 'order_form', target: 'ordered' },
  ordered_unverified: { hint: 'order_form', target: 'ordered' },
  invoice_source_conflict: { hint: 'invoice', target: 'invoice' },
};

function classify(rcv: OverviewReceiving): DocProblem[] {
  const rows: DocProblem[] = [];
  for (const need of rcv.needs) {
    const route = NEED_ROUTES[need.code];
    if (!route) continue;
    rows.push({
      key: `${rcv.receiptId}:need:${need.code}`,
      rcv,
      problem: need.text,
      hint: route.hint,
      target: route.target,
      humanOnly: false,
    });
  }
  for (const gap of rcv.documentGaps) {
    const route = GAP_ROUTES[gap.code];
    rows.push({
      key: `${rcv.receiptId}:gap:${gap.code}`,
      rcv,
      problem: gap.text,
      hint: route.hint,
      target: route.target,
      humanOnly: false,
    });
  }
  if (rcv.discrepancies.some((d) => d.action === 'source_invoice')) {
    rows.push({
      key: `${rcv.receiptId}:source_invoice`,
      rcv,
      problem: 'itemised invoice not in email — portal or rep only',
      hint: 'invoice',
      target: 'invoice',
      humanOnly: true,
    });
  }
  return rows;
}

/** The Ask-vendor draft's body (plain text; Mail threads it as a reply). */
function requestEmailText(rcv: OverviewReceiving, problem: string): string {
  return [
    'Hi,',
    '',
    `Could you send over an itemised copy of the invoice for our recent order${rcv.label ? ` (${rcv.label})` : ''}? `
      + `We're reconciling receiving #${rcv.receivingId} and are missing: ${problem}.`,
    '',
    'Thanks!',
  ].join('\n');
}

/**
 * Every document problem across every unbalanced receiving, in one place —
 * the rows that used to hide inside expanded detail panels and collapsed
 * buckets. Three exits per row: upload the file yourself, ask the vendor for
 * it (a reply draft in Mail), or send the orders run back to hunt the mailbox.
 */
export function DocumentsView({ overview, tasks, onChanged, onOpenJourney }: {
  overview: OrdersOverview;
  tasks: OpenAgentTask[];
  onChanged: () => void;
  onOpenJourney: (orderId: string) => void;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const huntTaskByOrder = useMemo(() => {
    const map = new Map<string, OpenAgentTask>();
    for (const t of tasks) if (t.kind === 'find_order_doc') map.set(t.orderId, t);
    return map;
  }, [tasks]);

  const { problems, agentPending } = useMemo(() => {
    const rows: DocProblem[] = [];
    let capturePending = 0;
    for (const rcv of overview.receivings) {
      if (rcv.balancedAt) continue;
      rows.push(...classify(rcv));
      capturePending += rcv.discrepancies.filter((d) => d.action === 'capture_lines').length;
    }
    // Human-only sourcing first — those rows exist because the automation
    // already exhausted the mailbox.
    rows.sort((a, b) => Number(b.humanOnly) - Number(a.humanOnly));
    return { problems: rows, agentPending: capturePending };
  }, [overview]);

  if (problems.length === 0 && agentPending === 0) {
    return <WorkEmpty>Every receiving has the documents it needs.</WorkEmpty>;
  }

  const humanOnly = problems.filter((p) => p.humanOnly);
  const sourceable = problems.filter((p) => !p.humanOnly);

  const row = (p: DocProblem) => {
    const hunt = huntTaskByOrder.get(p.rcv.orderId);
    return (
      <WorkRow
        key={p.key}
        scrollKey={`problem:${p.key}`}
        title={<JourneyName vendor={p.rcv.vendor} label={p.rcv.label} onOpen={() => onOpenJourney(p.rcv.orderId)} />}
        actions={
          <>
            <UploadDocumentButton
              orderId={p.rcv.orderId}
              docHint={p.hint}
              onUploaded={onChanged}
            />
            <Can code="orders:write:documents">
              <VendorDraftButton
                orderId={p.rcv.orderId}
                vendorEmail={p.rcv.vendorEmail}
                purpose="document"
                text={requestEmailText(p.rcv, p.problem)}
                label="Ask vendor"
              />
            </Can>
            {hunt ? (
              <PendingNudge task={hunt} onWithdrawn={onChanged} />
            ) : !p.humanOnly && (
              // POST /orders/:id/tasks — orders:write:nudges, which the page
              // code does not imply.
              <Can code="orders:write:nudges">
                <Hint content="Send the run back to hunt the mailbox for it.">
                  <RowAction
                    variant="quiet"
                    icon={<ScanSearch className="h-3 w-3" />}
                    busy={busyKey === p.key}
                    disabled={busyKey === p.key}
                    onClick={async () => {
                      setBusyKey(p.key);
                      try {
                        await createOrderTask(p.rcv.orderId, {
                          kind: 'find_order_doc',
                          payload: { target: p.target, receipt_id: p.rcv.receiptId },
                          note: `Re-hunt: ${p.problem} (receiving #${p.rcv.receivingId})`,
                        });
                        onChanged();
                      } finally {
                        setBusyKey(null);
                      }
                    }}
                  >
                    Re-hunt
                  </RowAction>
                </Hint>
              </Can>
            )}
          </>
        }
      >
        <span className="text-xs text-muted-foreground">#{p.rcv.receivingId}</span>
        <span className={cn('text-xs', p.humanOnly && 'font-medium text-foreground')}>
          {p.problem}
        </span>
      </WorkRow>
    );
  };

  return (
    <div>
      {humanOnly.length > 0 && (
        <WorkSection title="Only you can source these" count={humanOnly.length}>
          {humanOnly.map(row)}
        </WorkSection>
      )}

      {sourceable.length > 0 && (
        <WorkSection title="Missing or incomplete" count={sourceable.length}>
          {sourceable.map(row)}
        </WorkSection>
      )}

      {agentPending > 0 && (
        <WorkFootnote>
          {agentPending} product{agentPending === 1 ? '' : 's'} with the orders run for invoice capture.
        </WorkFootnote>
      )}
    </div>
  );
}
